import React, { useEffect,useRef,useState  } from 'react';

import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { Field, Form, FormSpy } from 'react-final-form';
import { useDispatch,useSelector  } from 'react-redux';

import { AppWrapper, Block, Coupon, FormCustomSelect, FormDatePicker, FormField } from 'components';
import AffiliateBusinessModal from 'components/AffiliateBusinessModal/AffiliateBusinessModal';
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs';
import Button from 'components/Button/Button';
import LLMCouponsPreview from 'components/LLMCouponsPreview/LLMCouponsPreview';
import Modal from 'components/Modal/Modal';
import { CouponCategoryField } from 'containers';
import CouponLocationField from 'containers/CouponLocationField/CouponLocationField';
// import styles from './LLMCouponsPreview.module.scss';
import { actions as affiliateActions } from 'coupon-common/src/modules/affiliateBusiness';
import { actions, duck } from 'coupon-common/src/modules/couponBuilder';
import { actions as couponImageActions } from 'coupon-common/src/modules/couponImages';
import { actions as locationActions, duck as locationsDuck } from 'coupon-common/src/modules/locations';
import { dispatchAsAsync, formatToISOString, getNormalizedEntity, parseISOString,required } from 'coupon-common/src/utils';

import AccordionSelector from './AccordionSelector'; // adjust path as needed




const initialPrompt = `You are an AI assistant that creates highly specific, image-based prompts to extract structured coupon data from retail flyers.

You will be shown a flyer image. Based on the layout, formatting, and content of that flyer, your task is to write a prompt that could be used by another AI model to extract structured JSON objects for all coupon deals that include a visible savings.

Your output should be a full, detailed instructional prompt that meets the following requirements:

---

 GOAL:
The resulting prompt should instruct another AI to extract **only** discounted deals and return them in this JSON format:

{
  "coupon_title": "",  // Title must be limited to 35 Characters!!! THIS IS IMPORTANT. Rewrite the title if necessary.
  "coupon_description": "", // Describe the product in a customer friendly way. Limit 200 characters.
  "deal_information": "Save $X.XX",  // or "Save XX%"
  "deal_price": "$X.XX",
  "start_date": "",  // extracted from the flyer footer
  "end_date": "",    // extracted from the flyer footer
  "category": ""     // one of the 5 categories below
  "is_affiliate_or_flyer": true // always true for this task
}

PROMPT MUST INCLUDE:

A short description of the flyer type (e.g., “grocery flyer from Walmart”)

Clear criteria for which deals to extract (only those with a visible discount)

Rules for how to format each field (see below)

Instructions for extracting start and end dates from the footer

Rules for assigning category using reasoning

A fixed list of 5 possible category values with examples for each

 CATEGORY VALUES (exactly one per item):

Things to do – Activities, events, experiences (e.g., live shows, escape rooms, classes)

Food & Drink – Any edible or drinkable item (e.g., soup, salmon, coffee)

Shopping – Physical goods not meant for consumption (e.g., detergent, clothing, toothpaste)

Services – Labor or support services (e.g., tax prep, oil change)

Self Care – Health and personal wellness (e.g., haircuts, dental care, skin care)

 FIELD FORMATTING:

deal_information must be: "$X.XX" or "XX%". Include no information beyond the price or percentage discount.

deal_price must include the numerical price and only the numerical price. It should be of the format X.XX and be displayed without any further information

start_date and end_date must be in "Month Day, Year" format, taken from the flyer footer. It will be in small print and difficult to find. Flyers are typically for a week.

Only include deals that have explicit savings, and skip any decorative or promo-only items

 OUTPUT FORMAT:

Output only the Step 2 prompt — a well-structured, instructional paragraph or series of bullet points that another AI model could use to extract the relevant JSON from the flyer.

Include small JSON samples or summaries and the instructional prompt for use in step 2.

 REMINDER:

This is a meta-task: You are writing a prompt that another AI will use to extract coupon data from the same flyer you're seeing now.


IMPORTANT: The final output must be a valid JSON array! Validate it before sending.`


function parseLLMResultsToCoupons(rawText) {
  let jsonArray = [];

  // Step 1: Trim down to JSON array if extra text is present
  const start = rawText.indexOf('[');
  const end = rawText.lastIndexOf(']');
  if (start === -1 || end === -1 || start >= end) {
    console.warn("⚠️ Could not find a valid JSON array in response.");
    return [];
  }

  const jsonText = rawText.slice(start, end + 1);

  try {
    jsonArray = JSON.parse(jsonText);
  } catch (err) {
    console.error("❌ JSON parse error:", err);
    return [];
  }

  const baseTimestamp = Date.now();

  // Step 2: Normalize and filter
  const coupons = jsonArray
    .map((item, index) => {
      const {
        coupon_title: title,
        coupon_description: description,
        deal_information: dealInfoRaw,
        deal_price: dealPriceRaw,
        start_date: startDate,
        end_date: endDate,
        category,
      } = item;

      let type = null;
      let amount_off = null;
      let percentage_off = null;

      const cleanDealPrice = parseFloat(dealPriceRaw?.replace(/[^\d.]/g, '')) || 0;

      if (/[%]/.test(dealInfoRaw)) {
        type = 'percentage_off';
        percentage_off = parseFloat(dealInfoRaw.replace(/[^\d.]/g, ''));
      } else {
        type = 'amount_off';
        amount_off = parseFloat(dealInfoRaw.replace(/[^\d.]/g, ''));
      }

      // const parsedStartDate = parseDate(startDate);
      // const parsedEndDate = parseDate(endDate);


      return {
        localId: `local-${baseTimestamp}-${index}`,
        title: title || '',
        description: description || '',
        deal_price: cleanDealPrice.toFixed(2),
        type,
        percentage_off,
        amount_off,
        start_date: parseISOString(startDate || ''),
        expiry_type: 'date',
        expiration_date: parseISOString(endDate || ''),
        published_at: parseISOString(startDate || ''),
        state: 'published',
        is_sponsored: false,
        is_blocked: false,
        user_limit: null,
        is_affiliate_or_flyer: true,
        remove: false,
        // category: { id: 1, name: "Clothing" },
      };
    })
    .filter((coupon) => {
      // Apply 10% off filter
      if (coupon.type === 'percentage_off') {
        return coupon.percentage_off >= 10;
      } else if (coupon.type === 'amount_off') {
        const deal = parseFloat(coupon.deal_price);
        const discount = parseFloat(coupon.amount_off);
        const originalPrice = deal + discount;
        const discountPercent = (discount / originalPrice) * 100;
        return discountPercent >= 10;
      }
      return false;
    });

  return coupons;
}

// const parseDate = (dateString) => {
//   if (!dateString) return null;
//   const parsedDate = new Date(dateString).toISOString().slice(0, 16);
//   return parsedDate;
// };

const parseDate = (dateString) => {
  if (!dateString) return null;

  const parsed = new Date(dateString);

  if (isNaN(parsed)) return null; // Invalid date

  return parsed;
};


const uploadManualCouponImages = async (uploadCoupons, dispatch, couponId = 0) => {
  const uploadedCoupons = [];

  for (let i = 0; i < uploadCoupons.length; i++) {
    const coupon = uploadCoupons[i];

    if (!coupon.uploadedFile) {
      uploadedCoupons.push({
        ...coupon,
        image_url: [],
      });
      continue;
    }

    try {
      const tempSlotId = i;
      const sequence = 1;

      // Upload image to S3 via backend
      const action = couponImageActions.uploadCouponImage(
        tempSlotId,
        couponId, // usually 0 if not saved yet
        sequence,
        coupon.uploadedFile
      );

      const result = await dispatchAsAsync(action, dispatch);

      if (result?.url) {
        uploadedCoupons.push({
          ...coupon,
          image_url: [{ image_url: result.url, sequence }],
        });
      } else {
        console.warn("Upload failed for coupon:", coupon.title);
        uploadedCoupons.push({
          ...coupon,
          image_url: [],
        });
      }
    } catch (err) {
      console.error("Manual upload error:", err);
      uploadedCoupons.push({
        ...coupon,
        image_url: [],
      });
    }
  }

  return uploadedCoupons;
};


// function parseEndDate(dateStr) {
//   const [monthName, dayStr] = dateStr.trim().split(' ');
//   const monthMap = {
//     January: 0, February: 1, March: 2, April: 3,
//     May: 4, June: 5, July: 6, August: 7,
//     September: 8, October: 9, November: 10, December: 11,
//   };

//   const day = parseInt(dayStr, 10);
//   const month = monthMap[monthName];

//   const now = new Date();
//   let year = now.getFullYear();

//   let parsed = new Date(Date.UTC(year, month, day));

//   // If parsed date is before today, assume it's next year
//   if (parsed < now) {
//     parsed = new Date(Date.UTC(year + 1, month, day));
//   }

//   // Optional: set to end of day UTC to avoid 00:00:00 causing issues
//   parsed.setUTCHours(23, 59, 59, 999);

//   return parsed;
// }


const FlyerCouponsTest = () => {
  const [imageUrl, setImageUrl] = useState('');
  const [response1, setResponse1] = useState('');
  const [response2, setResponse2] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveAllLoading, setSaveAllLoading] = useState(false);
  const [generateImages, setGenerateImages] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    all: false,
    fetchingDeals: false,
    generatingImages: false,
    saveAll: false,
  });
  const [response, setResponse] = useState('');
  const [parsedCoupons, setParsedCoupons] = useState([]);
  const [finalParsedCoupons, setFinalParsedCoupons] = useState([])
  const affiliateBusinesses = useSelector(state => {
  const data = state.affiliateBusiness?.data;
  if (Array.isArray(data)) return data;
  if (data?.results && Array.isArray(data.results)) return data.results;
  if (data && typeof data === 'object') return Object.values(data);
  return [];
});

  const [isModalOpen, setModalOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null); // null = add new
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [promptAddon, setPromptAddon] = useState('');
  const locations = useSelector(state => state.locations);

  const [provincesData, setProvincesData] = useState([]);
  const [citiesData, setCitiesData] = useState([]);

  const [selectedCityIds, setSelectedCityIds] = useState([]);

  const formValuesRef = useRef({});
  const formRefs = useRef({});
  const affiliateFormRef = useRef({});

  const imageModeOptions = [
    { id: 'create', name: 'Create Image (AI)' },
    { id: 'upload', name: 'Upload Image' },
    { id: 'noImage', name: 'No Image' },
  ];

  const selectedBusiness = affiliateBusinesses.find(
    (b) => b.id === selectedBusinessId
  );

  const dispatch = useDispatch();

  const provinceKey = locationsDuck.provincesEntity.key;

  const {
    entityList: provinces,
    isNextPageExist,
  } = getNormalizedEntity(locations, provinceKey);


  useEffect(() => {
    dispatch(affiliateActions.getAffiliateBusinesses());
  }, [dispatch]);

  const handleCouponChange = (index, field, value) => {
    setParsedCoupons((prev) =>
      prev.map((coupon, i) =>
        i === index ? { ...coupon, [field]: value } : coupon
      )
    );
  };


  const handleAdd = () => {
    setEditingBusiness(null);
    setModalOpen(true);
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setModalOpen(true);
  };

  const toggleRemove = (localId) => {

    const allCoupons = Object.values(formValuesRef.current);

    const updatedData = allCoupons.map((coupon) =>
      coupon.localId === localId
        ? {
          ...coupon,
          remove: !coupon.remove,
          imageMode: !coupon.remove
            ? { id: 'remove', name: 'Remove' }
            : { id: 'create', name: 'Create Image (AI)' },
        }
        : coupon
    );

    setParsedCoupons(updatedData);
  };


  const generateCouponImages = async (coupons, flyerImageUrl) => {
    const updated = [];

    for (const coupon of coupons) {
      const prompt = `From the provided flyer image, isolate the product described below and recreate it as accurately as possible. The output should depict only the product itself, with no background, text, price tags, promotional graphics, or surrounding elements. Render the product cleanly, centered, and against a transparent or solid white background.

  Product details:
  - Title: ${coupon.title}
  - Description: ${coupon.description}`;

      const formData = new FormData();
      formData.append('image_url', flyerImageUrl);
      formData.append('prompt', prompt);

      try {

        const res = await fetch(`${process.env.REACT_APP_BASE_URL}coupons/generate-coupon-image/`, {
          method: 'POST',
          body: formData,
        });

        const text = await res.text();
        const data = JSON.parse(text);

        if (!res.ok) {
          console.error('❌ API Error:', data);
          updated.push({ ...coupon, generated_image: null });
          continue;
        }

        const base64 = data?.data?.[0]?.b64_json;

        updated.push({
          ...coupon,
          generated_image: base64 ? `data:image/png;base64,${base64}` : null,
        });
      } catch (err) {
        console.error('❌ Image edit failed for coupon:', coupon, err);
        updated.push({ ...coupon, generated_image: null });
      }
    }

    return updated;
  };

const handleSaveAllBtn = async () => {
  
  const allCouponsData = [];
  const validationErrors = {};

  for (const key in formRefs.current) {
    if (formRefs.current.hasOwnProperty(key)) {
  
      const form = formRefs.current[key];

      if (form && typeof form.getState === 'function') {
        const couponData = form.getState().values;
        const state = form.getState();

  
        await form.submit();

        if (state.hasValidationErrors) {
          console.warn(`⚠️ Validation errors in form ${key}`, state.hasValidationErrors);
        }

        if (state.hasValidationErrors && !couponData.remove) {
          validationErrors[key] = state.hasValidationErrors;
        }

        if (!couponData.remove) {
          allCouponsData.push(couponData);
        }
      } else {
        console.warn(`❌ No form or getState function for key: ${key}`);
      }
    }
  }

  if (Object.keys(validationErrors).length === 0) {
    setLoadingStates((prev) => ({ ...prev, all: true, saveAll: true }));
    handleSaveAll(allCouponsData);
  } else {
    console.warn('🛑 Aborting save due to validation errors.');
  }
};

function dataURLToBlob(dataURL) {
  const parts = dataURL.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const binary = atob(parts[1]);
  const array = [];

  for (let i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i));
  }

  return new Blob([new Uint8Array(array)], { type: mime });
}



  const handleSaveAll = async (couponsData) => {

    const couponsToSave = couponsData;

    let successCount = 0;
    const failedCoupons = [];
    const successfulCouponIds = [];

    for (let i = 0; i < couponsToSave.length; i++) {
      const coupon = couponsToSave[i];
      const tempId = `auto-${Date.now()}-${Math.random()}`;

      let uploadedS3Id = null;

      try {
        // Upload image logic
        if (coupon.imageMode.id === 'upload' && coupon.uploadedFile) {
          const uploadAction = couponImageActions.uploadCouponImage(i, 0, 1, coupon.uploadedFile);
          const result = await dispatchAsAsync(uploadAction, dispatch);
         
          if (result?.id) uploadedS3Id = result.id;
        }

        if (coupon.imageMode.id === 'create' && coupon.generated_image) {
          const blob = dataURLToBlob(coupon.generated_image);
          const uploadAction = couponImageActions.uploadCouponImage(i, 0, 1, blob);
          const result = await dispatchAsAsync(uploadAction, dispatch);
          if (result?.id) uploadedS3Id = result.id;
        }

        // Prepare payload
        const payload = {
          ...coupon,
          price: parseFloat(coupon.deal_price),
          state: 'published',
          show_price_on_coupon: true,
          expiry_type: 'date',
          type: coupon.type || 'amount_off',
          business_id: selectedBusiness?.id,
          cities: selectedCityIds,
          coupon_images: uploadedS3Id ? [uploadedS3Id] : [],
        };

        const response = await dispatchAsAsync(actions.createCoupon(tempId, payload), dispatch);

        if (response?.success) {
          successCount++;
          successfulCouponIds.push(coupon?.localId); // collect
        }
        else if (!response.success) {

          // Extract a friendly error message
          let friendlyMessage = '';

          if (response?.error && typeof response.error === 'object') {
            friendlyMessage = Object.entries(response.error)
              .map(([field, messages]) => `[${field}]: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
              .join('; ');
          } else {
            friendlyMessage = response?.error?.message || 'Unknown error';
          }

          failedCoupons.push({
            index: i,
            title: coupon.title,
            error: friendlyMessage
          });
        }

      } catch (err) {
        failedCoupons.push({
          index: i,
          title: coupon.title,
          error: err?.message || 'Unexpected error occurred'
        });
      }
    }

    // Summary message
    let message = `${successCount} coupons saved ${successCount > 0 && `successfully`}.`;
    if (failedCoupons.length > 0) {
      message += ` ${failedCoupons.length} failed:\n\n` + failedCoupons.map(
        (f) => `- [Coupon: ${f.title || 'Untitled'}]  \n ${f.error}`
      ).join('\n');
    }

    alert(message);


    // 🧹 Remove all successful coupons from parsedCoupons at once and remove the ones that were set to be removed
    const unsuccessfulCoupons = couponsToSave.filter((coupon) => {
      return !successfulCouponIds.includes(coupon?.localId);
    })

    formRefs.current = {}
    formValuesRef.current = {}
    setParsedCoupons(unsuccessfulCoupons);
    setLoadingStates((prev) => ({ ...prev, all: false, saveAll: false }))

    if (unsuccessfulCoupons?.length === 0) {
      const confirm = window.confirm("There are no more coupons remaining to be processed. Do you want to reset all the fields ?")
      if (confirm) {
        setSelectedCityIds([])
        setSelectedBusinessId('')
        setImageUrl('')

        if (affiliateFormRef.current) {
          affiliateFormRef.current.reset();
        }
      }
      setResponse2('')
      setLoadingStates((prev) => ({ ...prev, all: false, generatingImages: false }))
    }

  };



  const callGPTWithImage = async (prompt, imageUrl) => {
    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('image', imageUrl);

      const res = await fetch(`${process.env.REACT_APP_BASE_URL}coupons/call-gpt-with-image/`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const status = res.status;
        let message = `Something went wrong.`;

        try {
          const error = await res.json();
          message = error?.error?.message || error?.message || message;
        } catch (error) {
          if (status === 404) message = `${status}. The requested resource was not found.`;
          else if (status === 500) message = `${status}. Internal server error. Please try again.`;
        }

        throw new Error(message);
      }

      const data = await res?.json();
      const result = data?.choices?.[0]?.message?.content
      if (!result) {
        throw new Error("The system was unable to extract coupon data from the provided image URL. Please check the image URL and try again.");
      }

      return result
    } catch (error) {
      throw error;
    }

  };



  const handleCouponImageUpload = (localId, file) => {
    const allCoupons = Object.values(formValuesRef.current);

    const updatedData = allCoupons.map((coupon) =>
      coupon.localId === localId
        ? {
          ...coupon,
          uploadedFile: file,
          imageMode: { id: 'upload', name: 'Upload Image' },
        }
        : coupon
    );

    setParsedCoupons(updatedData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingStates((prev) => ({ ...prev, all: true, fetchingDeals: true }))
    setResponse1('');
    setResponse2('');

    try {
      if (imageUrl.trim() === "") {
        alert("Url field is empty")
        setLoading(false)
        return
      }
      const fullPrompt = `${initialPrompt}\n\n---\n\n${promptAddon}`;
      const step1 = await callGPTWithImage(fullPrompt, imageUrl);

      setResponse1(step1);

      const step2 = await callGPTWithImage(step1, imageUrl);
      setResponse2(step2);

      const parsed = parseLLMResultsToCoupons(step2);
      setResponse(step2);

      setParsedCoupons(parsed.map(coupon => ({
        ...coupon,
        imageMode: imageModeOptions[0],     // default
        uploadedFile: null,      // initially null
        option: []
      })));

    } catch (err) {
      console.error(err);
      alert("Couldn't process your request due to an error.\nDetails: " + (err?.message || err));
      setResponse1('Error during GPT call.');
      setResponse2('');
    }

    setLoadingStates((prev) => ({ ...prev, all: false, fetchingDeals: false }))
  };

  useEffect(() => {
    const loadAllCities = async () => {
      try {
        await dispatch(locationActions.getCities()); // No args = fetch all cities
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      }
    };

    loadAllCities();
  }, [dispatch]);

  useEffect(() => {
    const { entityList: loadedCities } = getNormalizedEntity(
      locations,
      locationsDuck.citiesEntity.key
    );

    if (loadedCities?.length) {
      setCitiesData(loadedCities);
    }
  }, [locations]);


  useEffect(() => {
    if (provincesData.length || citiesData.length) {
    }
  }, [provincesData, citiesData]);


  useEffect(() => {
    const loadProvinces = async () => {
      await dispatch(locationActions.getProvinces());

      let more = true;
      while (more) {
        const { isNextPageExist } = getNormalizedEntity(
          locations,
          locationsDuck.provincesEntity.key
        );
        more = isNextPageExist;
        if (more) {
          await dispatch(locationActions.getProvincesNextPage());
        }
      }
    };

    loadProvinces();
  }, [dispatch]);

  useEffect(() => {
    const { entityList: loadedProvinces } = getNormalizedEntity(
      locations,
      locationsDuck.provincesEntity.key
    );

    if (loadedProvinces?.length) {
      setProvincesData(loadedProvinces);
    }
  }, [locations, dispatch]);

  const FileInputField = ({ input, meta, ...rest }) => {
    return (
      <div>
        <input
          type="file"
          accept="image/png, image/jpeg, image/webp"
          onChange={(e) => {
            const file = e.target.files?.[0] || null;
            input.onChange(file); // send the file into final-form
          }}
          {...rest}
        />
        {meta.touched && meta.error && (
          <p style={{ color: 'red' }}>{meta.error}</p>
        )}
      </div>
    );
  };

  const handleGenerateImagesBtn = async () => {
    setLoadingStates((prev) => ({ ...prev, all: true, generatingImages: true }))

    try {
      const allCoupons = Object.values(formValuesRef.current)

      const createCoupons = allCoupons.filter(c => c?.imageMode?.id === 'create');
      const uploadCoupons = allCoupons.filter(c => c?.imageMode?.id === 'upload');
      const noImageCoupons = allCoupons.filter(c => c?.imageMode?.id === 'noImage');
      const removeCoupons = allCoupons.filter(c => c.remove);

      const createdCoupons = await generateCouponImages(
        createCoupons,
        imageUrl,
      );

      // Combine all types
      const combined = [
        ...createdCoupons.map(c => ({
          ...c,
          image_url: [
            {
              image_url: c.generated_image, // base64 or hosted URL
              sequence: 1,
            },
          ],
        })),
        ...uploadCoupons.map(c => ({
          ...c,
          image_url: c.uploadedFile
            ? [
              {
                image_url: URL.createObjectURL(c.uploadedFile), // temp local preview
                sequence: 1,
              },
            ]
            : [],
        })),
        ...noImageCoupons.map(c => ({
          ...c,
          image_url: [],
        })),
        ...removeCoupons
      ];

      setParsedCoupons(combined);
    } catch (err) {
      alert(`Error generating or combining images \n Details: ${err?.message || err || "Something went wrong"}`)
    } finally {
      setLoadingStates((prev) => ({ ...prev, all: false, generatingImages: false }))
    }
  }


  return (
    <AppWrapper
      showHeader
      showSideNav
      showHeadingRow
      headingText={"GPT-4 Vision Coupon Prompt Generator"}
    >
      <div className='col-md-12' style={{ padding: 20 }}>
        <Breadcrumbs locationArray={['Admin', 'Flyer Import']} />
        <Button onClick={handleAdd} disabled={loadingStates.all}>Add New Business</Button>
        <div style={{ margin: "20px 0" }}>

          <Form
            initialValues={{ affiliateBusiness: null }}
            onSubmit={(values) => {
              const businessId = values?.affiliateBusiness?.id
              const selected = affiliateBusinesses.find((b) => b.id === businessId);
              if (selected) {
                handleEdit(selected);
              }
            }}
            render={({ handleSubmit, form }) => {
              affiliateFormRef.current = form; // 👈 Store ref
              return (
                <form className='d-flex align-items-center' onSubmit={handleSubmit}>
                  <div style={{ width: "100%", marginRight: 20 }}>
                    <Field
                      name={`affiliateBusiness`}
                      label="Affiliate Businesses"
                      placeholder="Select a business"
                      component={FormCustomSelect}
                      options={affiliateBusinesses}
                      parse={(value) => {
                        if (value?.id) {
                          setSelectedBusinessId(value.id);
                        } else {
                          setSelectedBusinessId('');
                        }
                        return value; // important! return the value so Final Form still updates
                      }}
                    />
                  </div>

                  <Button
                    style={{ marginLeft: 10 }}
                    disabled={!selectedBusinessId || loadingStates.all}
                  >
                    Edit Selected
                  </Button>
                </form>
              )
            }}
          />

        </div>

        {selectedBusinessId && (
          <div>

            <h4 style={{ marginTop: 30 }}>Target Cities</h4>

            <AccordionSelector
              provinces={provincesData}
              cities={citiesData}
              selectedCityIds={selectedCityIds}
              onChange={setSelectedCityIds}
            />

            {selectedCityIds?.length > 0 &&
              (
                <form onSubmit={handleSubmit}>
                  {/* URL input remains */}
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Paste flyer image URL"
                    style={{ width: '100%', padding: 8, marginBottom: 10 }}
                  />

                  <textarea
                    value={promptAddon}
                    onChange={(e) => setPromptAddon(e.target.value)}
                    placeholder="Optional: Add more instruction to the GPT prompt"
                    rows={3}
                    style={{ width: '100%', marginBottom: 10 }}
                  />

                  <Button
                    type="submit"
                    disabled={loadingStates.all}
                    fetching={loadingStates.fetchingDeals}
                  >
                    Extract Deals
                  </Button>
                  <span>{`>`}</span>
                  <Button
                    type="button"
                    onClick={handleGenerateImagesBtn}
                    disabled={loadingStates.all || parsedCoupons?.length === 0}
                    fetching={loadingStates.generatingImages}
                  >
                    Generate Images
                  </Button>
                  <span>{`>`}</span>
                  <Button
                    type="button"
                    disabled={parsedCoupons?.length === 0 || loadingStates.all}
                    onClick={handleSaveAllBtn}
                    fetching={loadingStates.saveAll}
                  >
                    Save All
                  </Button>
                </form>
              )}


          </div>
        )}

        {(selectedBusinessId && response2) && (
          <>
            <h3 style={{ marginTop: 30 }}>📝 Edit Parsed Coupons</h3>
            {parsedCoupons?.map((coupon, index) => (
              <Form
                key={coupon.localId}
                initialValues={coupon}
                onSubmit={() => { }}
                render={({ handleSubmit, values, form, hasValidationErrors }) => {

                  formRefs.current[coupon?.localId] = form;
                  formValuesRef.current[coupon?.localId] = values; // Collect each form's values

                  return (
                    <Block key={index} >

                      <FormSpy subscription={{ values: true }}>
                        {({ values }) => {
                          return null;
                        }}
                      </FormSpy>

                      <div
                        className="row mb-4"
                        style={{
                          padding: '10px',
                          marginBottom: '15px',
                          opacity: values?.remove ? 0.5 : 1,
                        }}
                      >
                        <div className="col-md-6">


                          <form onSubmit={handleSubmit} style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                          }}>
                            <div style={{
                              display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px'
                            }}>
                              <label><strong>{`Coupon #${index}`}</strong></label>

                              <Field
                                name={`imageMode`}
                                validate={required}
                                label="Image Option"
                                placeholder="Select an image option"
                                component={FormCustomSelect}
                                options={imageModeOptions}
                                readOnly={values?.imageMode?.id === 'remove' ?? false}
                              />
                            </div>
                            {values?.imageMode?.id === 'upload' && (
                              <Field name="uploadedFile">
                                {({ input }) => (
                                  <input
                                    required
                                    type="file"
                                    accept="image/png, image/jpeg, image/webp"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0] || null;
                                      input.onChange(file);
                                    }}
                                  />
                                )}
                              </Field>
                            )}

                            <div style={{ marginBottom: 8 }}>

                              <Field
                                name="title"
                                validate={required}
                                label="Title"
                                component={FormField}
                                type="text"
                                readOnly={values?.imageMode?.id === 'remove'}
                              />
                            </div>

                            <div style={{ marginBottom: 8 }}>

                              <Field
                                name="description"
                                validate={required}
                                label="Description"
                                component={FormField}
                                type="text"
                                readOnly={values?.imageMode?.id === 'remove'}
                              />
                            </div>

                            <div style={{ marginBottom: 8 }}>

                              <Field
                                name="amount_off"
                                validate={required}
                                label="Amount Off"
                                component={FormField}
                                type="text"
                                readOnly={values?.imageMode?.id === 'remove'}
                              />
                            </div>


                            <Field
                              name="published_at"
                              validate={required}
                              label="Start Date"
                              component={FormDatePicker}
                              readOnly={values?.imageMode?.id === 'remove'}
                            />

                            <div style={{ marginBottom: 8 }}>

                              <Field
                                name="expiration_date"
                                validate={required}
                                label="End Date"
                                component={FormDatePicker}
                                readOnly={values?.imageMode?.id === 'remove'}
                              />
                            </div>
                            <div style={{
                              marginBottom: 8,
                              pointerEvents: values?.imageMode?.id === 'remove' ? 'none' : 'auto',
                            }}>
                              <CouponCategoryField />
                            </div>
                            <Field name="generated_image" element="input" type="hidden" />

                          </form>
                        </div>

                        <div className="col-md-5">
                          <LLMCouponsPreview
                            coupon={values}
                            business={selectedBusiness}
                          />
                        </div>
                      </div>

                      <FormSpy subscription={{ submitFailed: true, hasValidationErrors: true }}>
                        {({ submitFailed, hasValidationErrors }) => (
                          submitFailed && hasValidationErrors && !values.remove ? (
                            <label style={{ marginLeft: 12, color: '#de3e4e' }}>{`Some required fields are missing or contain errors. Please review the form and correct the highlighted sections.`}</label>
                          ) : null
                        )}
                      </FormSpy>

                      <div style={{ marginLeft: 12, marginTop: 4 }}>
                        <label>
                          <input
                            type="checkbox"
                            checked={values?.remove}
                            onChange={() => toggleRemove(values?.localId)}
                          />{" "}
                          Remove?
                        </label>
                      </div>
                    </Block>
                  )
                }
                }
              />
            ))}

          </>
        )}
        <AffiliateBusinessModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          business={editingBusiness}
        />
      </div>
    </AppWrapper>
  );
};

export default FlyerCouponsTest;


